import { getRequestDeliverableLabel } from '../types/requestDeliverables';

import type { FieldInfoPanel } from '../types/fieldInfoPanel';
import type { RequestDeliverableType } from '../types/requestDeliverables';

/**
 * Copy source: Site_scope_App-_Artifacts_inputs.xlsx sheet "Deliverable - helper text"
 * (GitLab #1473 upload, last modified Aman Khan 2026-08-21). Strings are verbatim.
 * Overlay title at the call site is getRequestDeliverableLabel, not the workbook
 * Deliverable punctuation. INSERT_MAX is an appendExampleText rail only — do not
 * put maxLength on the comment TextInput.
 */
export const DELIVERABLE_COMMENT_INSERT_MAX = 100_000;

/** Budgetary Quote and BOM share this Helper Text; keep one literal so they cannot drift. */
const BUDGETARY_AND_BOM_HELPER_TEXT =
  'Specify total IT load, kW/rack, number of racks, voltage, redundancy, cooling type, options, services, product preference.';

const helper = (
  type: RequestDeliverableType,
  wouldReceive: string,
  helperText: string,
): FieldInfoPanel => ({
  title: getRequestDeliverableLabel(type),
  bullets: [wouldReceive],
  examplesIntro: 'Here is an example to get you started:',
  examples: [{ id: type, label: wouldReceive, text: helperText }],
});

export const DELIVERABLE_COMMENT_INFO: Record<
  RequestDeliverableType,
  FieldInfoPanel
> = {
  BUDGETARY_QUOTE: helper(
    'BUDGETARY_QUOTE',
    'Indicative pricing for planning',
    BUDGETARY_AND_BOM_HELPER_TEXT,
  ),
  BOM_ONLY: helper(
    'BOM_ONLY',
    'Equipment list with SKUs and quantities with pricing',
    BUDGETARY_AND_BOM_HELPER_TEXT,
  ),
  FLOOR_PLAN: helper(
    'FLOOR_PLAN',
    '2D rack and equipment placement drawing',
    'Provide room dimensions, floor plan (CAD/PDF), raised floor height, containment, clearances, doors, constraints.',
  ),
  SINGLE_LINE_DIAGRAM_POWER: helper(
    'SINGLE_LINE_DIAGRAM_POWER',
    'Electrical one-line from utility to rack',
    'Specify voltage, phases, redundancy, existing UPS/ATS/PDU configuration.',
  ),
  MONITORING_DIAGRAM: helper(
    'MONITORING_DIAGRAM',
    'Device communication & monitoring architecture',
    'Specify existing equipment list, protocol, cooling, sensors and BMS/DCIM.',
  ),
  FLUID_PIPING_DIAGRAM: helper(
    'FLUID_PIPING_DIAGRAM',
    'Piping layout — CDUs, routes, valves, pumps, sensors',
    'Provide existing cooling type, flow rate, containment, pump redundancy, room dimensions, floor plan, rack layout preference, clearances, doors, constraints.',
  ),
  RENDERING_3D: helper(
    'RENDERING_3D',
    'Photorealistic 3D views for stakeholder presentations',
    'Specify room dimensions, floor plan (CAD/PDF), raised floor height, containment, clearances, cable routing, doors, constraints.',
  ),
  OTHER: helper(
    'OTHER',
    'Describe what you need (e.g., CFD analysis, technical spec sheet)',
    'Describe your requirement and provide relevant technical details.',
  ),
};

/** Catalogue enum key, not user input — same pattern as getRequestDeliverableLabel. */
export function getDeliverableCommentInfo(
  type: RequestDeliverableType,
): FieldInfoPanel {
  // eslint-disable-next-line security/detect-object-injection
  return DELIVERABLE_COMMENT_INFO[type];
}
